import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Box,
  InputAdornment,
  IconButton,
  Collapse,
} from "@mui/material";
import {
  Person,
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  Close,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { axiosService } from "shared/helpers/axios";
import { refreshUserData } from "shared/contexts/UserContext";

// ── Styled components (matching LoginForm) ──

const StyledPaper = styled(Paper)(() => ({
  display: "flex",
  flexDirection: "column",
  padding: "28px 32px",
  width: 450,
  height: "auto",
  textAlign: "center",
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(10px)",
  borderRadius: 16,
  boxShadow: "0 8px 32px rgba(118, 75, 162, 0.15)",
  transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 12px 48px rgba(118, 75, 162, 0.25)",
  },
}));

const StyledTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    transition: "all 0.2s ease-in-out",
    fontSize: "1.3rem",
    borderRadius: 12,
    "&:hover": {
      boxShadow: "0 4px 12px rgba(118, 75, 162, 0.1)",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(118, 75, 162, 0.5)",
      },
    },
    "&.Mui-focused": {
      transform: "translateY(-1px)",
      boxShadow: "0 6px 16px rgba(118, 75, 162, 0.15)",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(118, 75, 162, 0.55)",
        borderWidth: 1,
      },
    },
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active": {
      WebkitBoxShadow: "0 0 0 100px white inset",
      WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
      caretColor: "rgba(0, 0, 0, 0.87)",
      backgroundColor: "transparent !important",
      transition: "background-color 5000s ease-in-out 0s",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "rgba(118, 75, 162, 0.95)",
  },
  "& .MuiFormHelperText-root": {
    margin: "2px 0 0 0",
    fontSize: "0.7rem",
  },
}));

const StyledButton = styled(Button)(() => ({
  color: "#fff",
  backgroundColor: "rgba(118, 75, 162, 0.85)",
  padding: "10px 24px",
  borderRadius: 12,
  textTransform: "none",
  fontSize: "0.95rem",
  fontWeight: 600,
  boxShadow: "0 4px 12px rgba(118, 75, 162, 0.25)",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "rgba(91, 54, 128, 0.95)",
    transform: "translateY(-2px)",
    boxShadow: "0 6px 16px rgba(118, 75, 162, 0.35)",
  },
  "&:active": {
    transform: "translateY(0)",
    boxShadow: "0 2px 8px rgba(118, 75, 162, 0.25)",
  },
  "&:disabled": {
    backgroundColor: "rgba(118, 75, 162, 0.4)",
    color: "rgba(255, 255, 255, 0.7)",
  },
}));

// ── Component ──

// initialEmail: from an invitation link (/login/?signup=1&email=…), so the invited address is used
function RegistrationForm({ onSwitchToLogin, initialEmail = "" }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: initialEmail,
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState(null);
  const [externalConflict, setExternalConflict] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setLoading(true);
    setExternalConflict(false);
    try {
      await axiosService.post("/auth/register/", {
        username: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        password: formData.password,
        email: formData.email,
      });
      await refreshUserData();
      navigate("/");
    } catch (err) {
      console.error("Registration error:", err);
      const d = err.response?.data;
      if (err.response?.status === 409 && d?.is_external_conflict) {
        setExternalConflict(true);
        return;
      }
      const errorMessage =
        d?.error ||
        d?.message ||
        d?.email?.[0] ||
        d?.password?.[0] ||
        d?.first_name?.[0] ||
        d?.last_name?.[0] ||
        "Registration failed. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      style={{ position: "relative", zIndex: 1 }}
    >
      <StyledPaper elevation={0}>
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 0.5,
            }}
          >
            Create Account
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Sign up to get started
          </Typography>

          {/* External profile conflict */}
          <Collapse in={externalConflict}>
            <Alert
              severity="info"
              action={
                <IconButton size="small" onClick={() => setExternalConflict(false)} aria-label="Dismiss">
                  <Close fontSize="small" />
                </IconButton>
              }
              sx={{ mb: 1.5, borderRadius: 2, textAlign: 'left' }}
            >
              <strong>This email is already linked to a consortium profile.</strong>
              <br />
              A consortium coordinator has added you as an external member. Go to the{' '}
              <Button
                variant="text"
                size="small"
                onClick={() => navigate('/login/')}
                sx={{ p: 0, minWidth: 'auto', fontWeight: 600, verticalAlign: 'baseline', textTransform: 'none', fontSize: 'inherit' }}
              >
                login page
              </Button>{' '}
              and use <strong>Forgot Password</strong> to set your password and activate your account.
            </Alert>
          </Collapse>

          {/* Error */}
          <AnimatePresence>
            {error && (
              <Collapse in={!!error}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                >
                  <Alert
                    severity="error"
                    action={
                      <IconButton size="small" onClick={() => setError(null)} aria-label="Dismiss error">
                        <Close fontSize="small" />
                      </IconButton>
                    }
                    sx={{ mb: 1.5, borderRadius: 2, fontSize: "1rem" }}
                  >
                    {error}
                  </Alert>
                </motion.div>
              </Collapse>
            )}
          </AnimatePresence>

          {/* Fields */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {/* Name row */}
            <Box sx={{ display: "flex", gap: 1.25 }}>
              <StyledTextField
                size="small"
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                disabled={loading}
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ fontSize: 20, color: "rgba(118,75,162,0.5)" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <StyledTextField
                size="small"
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                disabled={loading}
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ fontSize: 20, color: "rgba(118,75,162,0.5)" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <StyledTextField
              fullWidth
              size="small"
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ fontSize: 20, color: "rgba(118,75,162,0.5)" }} />
                  </InputAdornment>
                ),
              }}
            />

            <StyledTextField
              fullWidth
              size="small"
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              required
              disabled={loading}
              helperText="At least 8 characters"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ fontSize: 20, color: "rgba(118,75,162,0.5)" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword((p) => !p)}
                      edge="end"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <VisibilityOff sx={{ fontSize: 20 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 20 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <StyledTextField
              fullWidth
              size="small"
              label="Confirm Password"
              name="confirmPassword"
              type={showConfirm ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ fontSize: 20, color: "rgba(118,75,162,0.5)" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowConfirm((p) => !p)}
                      edge="end"
                      aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
                    >
                      {showConfirm ? (
                        <VisibilityOff sx={{ fontSize: 20 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 20 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Submit */}
          <StyledButton
            fullWidth
            type="submit"
            disabled={loading}
            sx={{ mt: 2.5 }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: "rgba(255,255,255,0.8)" }} />
            ) : (
              "Create Account"
            )}
          </StyledButton>

          {/* Footer */}
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            Already have an account?{" "}
            <Button
              variant="text"
              onClick={() => onSwitchToLogin ? onSwitchToLogin() : navigate("/login/")}
              sx={{
                color: "rgba(118, 75, 162, 0.95)",
                fontWeight: 600,
                textTransform: "none",
                fontSize: "inherit",
                p: 0,
                minWidth: "auto",
                verticalAlign: "baseline",
                "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
              }}
            >
              Sign In
            </Button>
          </Typography>
        </form>
      </StyledPaper>
    </motion.div>
  );
}

export default RegistrationForm;